/**
 * The one loading screen.
 *
 * It began as the first thing a visitor sees, for the moment it takes Clerk
 * to say who they are. Then a route on its way drew a bare ring 200px from
 * the top, a gated page drew a purple ring with "Loading feature..." in the
 * middle of the content column, and a few pages drew a third ring of their
 * own. On one first load a person watched the spinner change shape and jump
 * around the window three times before anything arrived.
 *
 * So there is one: the mark, the name, and the ring the rest of the product
 * uses, with a few words when there is something specific to say. Every
 * wait that takes the whole page draws this and nothing else. Waits inside a
 * page that keep their layout (a table filling, a tab body) keep the plain
 * ring, and skeletons that hold a page's shape stay as they are.
 *
 * Two placements. On its own it takes the window, for the moments before
 * there is any shell. Inside the shell it takes the content area under the
 * app bar instead, so the bar stays put and the mark lands within a few
 * pixels of where it was a moment ago.
 *
 * A bare `.ds` scope with no `data-ds-theme`, so it follows the `dark` class
 * on the document the way every island does. It renders before any provider
 * that could tell it more.
 */
import { Spinner } from '@/design-system/feedback'
import { Mark } from '@/design-system/shell'
import { cn } from '@/utils/cn'
import '@/design-system/tokens.css'
import '@/design-system/components.css'
import '@/design-system/feedback.css'

export function AuthLoadingScreen({
  words,
  note,
  inShell = false,
}: {
  /** What is being waited for, in a few words. Read out as the status. */
  words?: string
  /** One sentence under the words, when the wait needs explaining. */
  note?: string
  /** Under the app bar rather than over the whole window. */
  inShell?: boolean
} = {}) {
  return (
    <div
      className={cn('ds ds-boot', inShell && 'ds-boot--shell')}
      role="status"
      aria-label={words ?? 'Loading SuperSchema'}
    >
      <div className="ds-boot__in">
        <div className="ds-boot__brand" aria-hidden="true">
          <Mark size={30} />
          <span>SuperSchema</span>
        </div>
        <Spinner lg quiet />
        {words && (
          <div className="ds-boot__words" aria-hidden="true">
            <div className="ds-boot__what">{words}</div>
            {note && <p className="ds-boot__why">{note}</p>}
          </div>
        )}
      </div>
    </div>
  )
}
